import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import type { Resource, ResourceCategory } from "@shared/types";
import { formatAud } from "@shared/types";
import { PLANT_SPECIES_ID, isPlantSpeciesIdSlug } from "@shared/plantSpeciesId";
import {
  resourceDetailTaxNote,
  resourceListTaxNote,
  resourcePriceAmount,
} from "@shared/sgdDiyPrices";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export function ResourcesPage() {
  const [resources, setResources] = useState<Resource[]>([]);
  const [categories, setCategories] = useState<ResourceCategory[]>([]);
  const [q, setQ] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const category = params.get("category") ?? "";
  const { user } = useAuth();

  useEffect(() => {
    void (async () => {
      try {
        const [cats, res] = await Promise.all([
          api.categories(),
          api.resources({ category: category || undefined, q: q || undefined }),
        ]);
        setCategories(cats.categories);
        setResources(res.resources);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load resources");
      }
    })();
  }, [category, q]);

  return (
    <section className="section">
      <div className="container">
        <div className="section-head">
          <div>
            <p className="eyebrow">DIY · Do it yourself</p>
            <h2>Tools you can use on site today</h2>
            <p>
              Controlled templates aligned to Australia environmental and Singapore MEP services —
              checklists, canvases and worksheets. Download and go, or escalate to DIWY / DIFY when
              you need specialist delivery.
            </p>
          </div>
          {!user ? (
            <Link className="btn btn-outline" to="/login">
              Sign in to download
            </Link>
          ) : null}
        </div>

        {error ? <div className="alert alert-error">{error}</div> : null}

        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1.25rem" }}>
          <input
            placeholder="Search resources…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ maxWidth: 280 }}
          />
          <select
            value={category}
            onChange={(e) => {
              const v = e.target.value;
              if (v) setParams({ category: v });
              else setParams({});
            }}
            style={{ maxWidth: 260 }}
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.slug}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid-3">
          {[...resources]
            .sort((a, b) => Number(isPlantSpeciesIdSlug(b.slug)) - Number(isPlantSpeciesIdSlug(a.slug)))
            .map((r) => (
            <article key={r.id} className="card card-hover">
              <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem" }}>
                <span className={`badge ${r.isFree ? "badge-free" : "badge-paid"}`}>
                  {isPlantSpeciesIdSlug(r.slug) ? "Photo ID" : r.isFree ? "Free" : "Paid"}
                </span>
                <span className="muted" style={{ fontSize: "0.8rem" }}>
                  {r.categoryName}
                </span>
              </div>
              <h3 style={{ marginTop: "0.75rem" }}>{r.title}</h3>
              <p>{r.summary}</p>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "1rem",
                  gap: "0.5rem",
                }}
              >
                <div className="price">
                  {resourcePriceAmount(r)}{" "}
                  {resourceListTaxNote(r) ? <small>{resourceListTaxNote(r)}</small> : null}
                </div>
                <Link className="btn btn-outline btn-sm" to={`/resources/${r.slug}`}>
                  View
                </Link>
              </div>
            </article>
          ))}
        </div>
        {!resources.length && !error ? (
          <p className="muted">No resources match your filters.</p>
        ) : null}
      </div>
    </section>
  );
}

export function ResourceDetailPage() {
  const { slug = "" } = useParams();
  const [resource, setResource] = useState<Resource | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    void api
      .resource(slug)
      .then((r) => setResource(r.resource))
      .catch((e) => setError(e instanceof Error ? e.message : "Not found"));
  }, [slug]);

  async function handleAcquire() {
    if (!resource) return;
    if (!user) {
      window.location.href = `/login?next=/resources/${resource.slug}`;
      return;
    }
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const res = await api.purchaseResource(resource.id);
      if (res.checkoutUrl) {
        window.location.href = res.checkoutUrl;
        return;
      }
      if (isPlantSpeciesIdSlug(resource.slug) && res.submissionId) {
        window.location.href = `/dashboard/reports/${res.submissionId}`;
        return;
      }
      if (isPlantSpeciesIdSlug(resource.slug) && (res.alreadyOwned || res.ok)) {
        window.location.href = `/reports?type=${encodeURIComponent(resource.slug)}`;
        return;
      }
      if (res.free || res.alreadyOwned || res.ok) {
        await api.downloadResource(resource.id, resource.fileName ?? undefined);
        setInfo(res.alreadyOwned ? "Already in your library. Download started." : "Download started.");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Purchase failed");
    } finally {
      setBusy(false);
    }
  }

  const intake = resource ? isPlantSpeciesIdSlug(resource.slug) : false;

  if (error && !resource) {
    return (
      <section className="section container">
        <div className="alert alert-error">{error}</div>
      </section>
    );
  }
  if (!resource) {
    return (
      <section className="section container">
        <p className="muted">Loading…</p>
      </section>
    );
  }

  return (
    <section className="section">
      <div className="container" style={{ maxWidth: 760 }}>
        <Link to="/resources" className="muted">
          ← Library
        </Link>
        <div className="card" style={{ marginTop: "1rem" }}>
          <span className={`badge ${resource.isFree ? "badge-free" : "badge-paid"}`}>
            {intake ? "Photo ID" : resource.isFree ? "Free" : "Paid"}
          </span>
          <h1 style={{ marginTop: "0.75rem" }}>{resource.title}</h1>
          <p className="muted">{resource.categoryName}</p>
          <p>{resource.description || resource.summary}</p>
          {intake ? (
            <ul className="check-list" style={{ marginTop: "1rem" }}>
              <li>{PLANT_SPECIES_ID.photoCopy}</li>
              <li>{PLANT_SPECIES_ID.fewerCopy}</li>
              <li>{PLANT_SPECIES_ID.deliveryCopy}</li>
              <li>{PLANT_SPECIES_ID.distinctFrom}</li>
            </ul>
          ) : null}
          <p className="price" style={{ margin: "1rem 0" }}>
            {resourcePriceAmount(resource)}{" "}
            {resourceDetailTaxNote(resource) ? (
              <small>{resourceDetailTaxNote(resource)}</small>
            ) : null}
          </p>
          {error ? <div className="alert alert-error">{error}</div> : null}
          {info ? <div className="alert alert-success">{info}</div> : null}
          <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
            <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void handleAcquire()}>
              {intake
                ? user
                  ? `Pay ${formatAud(resource.priceCents)} and start intake`
                  : "Sign in to buy"
                : resource.isFree
                  ? user
                    ? "Download now"
                    : "Sign in to download"
                  : "Purchase & download"}
            </button>
          </div>
          {resource.isFree ? (
            <p className="muted" style={{ marginTop: "0.85rem", fontSize: "0.88rem" }}>
              Free tools stay $0. Sign in with your existing account (name and email) to download
              and keep a copy in your library.
            </p>
          ) : null}
          {intake ? (
            <p className="muted" style={{ marginTop: "0.85rem", fontSize: "0.88rem" }}>
              After payment you upload 3 photos (or fewer if that is all you have) so the practice
              can identify the plant.
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}
